export const calculateSumOfEachField = (arrayOfObjects: Array<Object>) => {
  return arrayOfObjects.reduce((acc, curr) => {
    Object.keys(curr).forEach((rowKey) => {
      const value = curr[rowKey];
      if (!acc[rowKey]) {
        acc[rowKey] = 0;
      }
      acc[rowKey] += value;
    });

    return acc;
  }, {});
};

export const mapValuesByField = (arrayOfObjects: Array<Object>) => {
  return arrayOfObjects.reduce((acc, curr) => {
    Object.keys(curr).forEach((c) => {
      const value = curr[c];
      if (!acc[c]) {
        acc[c] = [];
      }
      acc[c].push(value);
    });
    return acc;
  }, {});
};

export const weightedAverage = (
  nums: Array<number> = [],
  weights: Array<number> = [],
) => {
  const [sum, weightSum] = weights.reduce(
    (acc, w, i) => {
      acc[0] += nums[i] * w;
      acc[1] += w;
      return acc;
    },
    [0, 0],
  );
  return sum / weightSum;
};

/**
 * Extracts unique development phases from portfolio results
 * @param {Array} results - Array of portfolio objects
 * @returns {Array} Array of unique development phase options for filter
 */
export const extractUniqueDevelopmentPhases = (results: Array<Object>) => {
  const phases = new Set();

  results.forEach((row) => {
    if (row.developmentPhase && row.developmentPhase !== 'Not Provided') {
      phases.add(row.developmentPhase);
    }
  });

  return Array.from(phases)
    .sort()
    .map((phase) => ({
      text: phase,
      value: phase,
    }));
};

/**
 * Extracts unique property types from portfolio results
 * Returns only the three main property types: Affordable, Conventional, Commercial
 * @param {Array} results - Array of portfolio objects
 * @returns {Array} Array of unique property type options for filter
 */
export const extractUniquePropertyTypes = (results: Array<Object>) => {
  // Fixed set of property type options
  const PROPERTY_TYPE_OPTIONS = [
    { text: 'Affordable', value: 'affordable' },
    { text: 'Commercial', value: 'commercial' },
    { text: 'Conventional', value: 'conventional' },
  ];

  return PROPERTY_TYPE_OPTIONS;
};

/**
 * Custom filter function for development phases
 * @param {string[]} filterByArr - Array of selected development phases
 * @param {string} filterValue - Development phase value for a row
 * @returns {boolean} True if the row's phase matches any selected phase
 */
export const developmentPhaseFilterFn = (
  filterByArr: Array<string>,
  filterValue: string,
) => {
  if (!filterByArr || filterByArr.length === 0) {
    return true; // No filter applied, show all
  }

  if (!filterValue || filterValue === 'Not Provided') {
    return false; // Hide rows without development phase if filter is active
  }

  // Check if the row's development phase is in the selected filters
  // Note: filterByArr is already lowercase from the table component
  return filterByArr.includes(filterValue.toLowerCase());
};

/**
 * Custom filter function for property types
 * Handles comma-separated property type values (e.g., "Affordable, Commercial")
 * @param {string[]} filterByArr - Array of selected property types (lowercase)
 * @param {string} filterValue - Property type value for a row (may be comma-separated)
 * @returns {boolean} True if any selected type is found in the row's property types
 */
export const propertyTypeFilterFn = (
  filterByArr: Array<string>,
  filterValue: string,
) => {
  if (!filterByArr || filterByArr.length === 0) {
    return true; // No filter applied, show all
  }

  if (!filterValue || filterValue === 'Not Provided') {
    return false; // Hide rows without property type if filter is active
  }

  // Split comma-separated values and convert to lowercase for comparison
  const rowPropertyTypes = filterValue
    .split(',')
    .map((type) => type.trim().toLowerCase());

  // Check if any selected filter value is contained in the row's property types
  // Note: filterByArr is already lowercase from the table component
  return filterByArr.some((selectedType) =>
    rowPropertyTypes.includes(selectedType),
  );
};
